import { AnyAction } from 'redux';
import { useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { option } from 'fp-ts';

import { toggleViewMode, validateQuery } from 'modules/queryBuilder/legacyQueryBuilder/actions';
import { catalogPreviewQueryRequest } from 'modules/catalog/catalogPreview';
import { useCatalogContext } from 'modules/catalog/catalogSelector/catalogContext';
import { SearchQuery } from 'modules/domains';
import { NoOp as SideEffect } from 'utils/noop';

import { useGetCatalogFieldDetails } from './useGetCatalogFieldDetails';
import { useCatalogPreviewContext } from '../contexts/CatalogPreviewContext';
import { useGetCatalogQuery } from './useGetCatalogQuery';
import { useAngularCatalogQueryContext } from '../../contexts/AngularCatalogQueryContext';

export const useFooterHandlers = () => {
  const dispatch = useDispatch();
  const catalogQuery = useGetCatalogQuery();
  const catalogFieldDetails = useGetCatalogFieldDetails();
  const { saveAndClose } = useAngularCatalogQueryContext();
  const {
    catalog,
    catalogFieldDetails: catalogField,
    updateCatalogError,
    updateCatalogFieldError,
  } = useCatalogContext();
  const { openPreview, closePreview } = useCatalogPreviewContext();
  const handleToggleViewMode = () => dispatch(toggleViewMode() as AnyAction);

  const catalogPreviewQuery = (
    catalogName?: string,
    searchQuery?: SearchQuery,
    sideEffectFn?: SideEffect,
  ) => {
    dispatch(catalogPreviewQueryRequest(catalogName, searchQuery));
    if (sideEffectFn) sideEffectFn(); // call side effect fn if exists
  };

  const updateSelectorErrors = () => {
    updateCatalogError();
    if (option.isSome(catalog)) updateCatalogFieldError(); // only update catalog field error state(to true) if a catalog is already selected.
  };

  const dispatchValidateQuery = (sideEffectFn: SideEffect) => {
    dispatch(
      validateQuery(
        catalogPreviewQuery,
        catalogQuery?.catalogName,
        catalogQuery?.searchQuery,
        sideEffectFn,
      ) as AnyAction,
    );
  };

  const validateCatalogQuery = (sideEffectFn: SideEffect) =>
    option.isNone(catalog) || option.isNone(catalogField)
      ? updateSelectorErrors()
      : dispatchValidateQuery(sideEffectFn);

  const onSave = () => {
    saveAndClose({ catalogQuery, catalogFieldDetails });
    closePreview();
    handleToggleViewMode();
  };

  const onApplyQuery = useCallback(
    () => validateCatalogQuery(onSave),
    [catalogFieldDetails, catalogQuery],
  );
  const onPreviewOpen = useCallback(
    () => validateCatalogQuery(openPreview),
    [catalogFieldDetails, catalogQuery],
  );
  return {
    onApplyQuery,
    onPreviewOpen,
  };
};
